import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle2, ArrowRight, Users, FileText, Globe, Star } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const OFFER_ICONS = [Users, FileText, Globe, Star];

export default function Partnerships() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const offerItems = t('partnerships.offer_items', { returnObjects: true }) || [];
  const processSteps = t('partnerships.process_steps', { returnObjects: true }) || [];
  const requirementsItems = t('partnerships.requirements_items', { returnObjects: true }) || [];
  const faqs = t('partnerships.faqs', { returnObjects: true }) || [];

  return (
    <>
      <Helmet>
        <title>Partnerships — ENERGIESSE SL Solar Subcontracting</title>
        <meta
          name="description"
          content="Partner with ENERGIESSE SL for solar installation subcontracting in Germany, Austria, Switzerland and Spain. Two teams, EU-ready, complete documentation."
        />
      </Helmet>

      {/* Page Hero */}
      <section className="pt-14 pb-10 bg-[var(--energy-bg)] border-b border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl space-y-4"
          >
            <motion.p variants={fadeUp} className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
              {t('nav.partnerships')}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('partnerships.headline')}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base text-muted-foreground leading-relaxed max-w-[60ch]">
              {t('partnerships.subtitle')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Button
                size="lg"
                className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary h-11"
                onClick={() => navigate('/contact')}
                data-testid="partnerships-hero-cta"
              >
                {t('partnerships.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What we offer */}
      <section className="section-padding bg-white">
        <div className="container-site">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-8"
          >
            <motion.div variants={fadeUp} className="space-y-1">
              <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                {t('partnerships.offer_title')}
              </p>
            </motion.div>

            <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {offerItems.map((item, i) => {
                const Icon = OFFER_ICONS[i] || CheckCircle2;
                return (
                  <motion.div key={i} variants={fadeUp}>
                    <Card className="border-border card-hover h-full">
                      <CardContent className="p-6 space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-[var(--energy-green-soft)] flex items-center justify-center">
                          <Icon className="h-5 w-5 text-[var(--energy-green)]" />
                        </div>
                        <h3 className="font-heading font-semibold text-base">{item.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-[var(--energy-bg)] border-y border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-8"
          >
            <motion.div variants={fadeUp} className="space-y-1">
              <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                {t('partnerships.process_title')}
              </p>
            </motion.div>

            <motion.div variants={stagger} className="space-y-4">
              {processSteps.map((step, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex items-start gap-5 p-5 rounded-xl border border-border bg-white"
                >
                  <div className="flex-shrink-0">
                    <span className="stat-number text-2xl font-semibold text-[var(--energy-green)]">
                      {step.step}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-heading font-semibold text-sm">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Ideal partner + FAQ */}
      <section className="section-padding bg-white">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Requirements */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-1">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('partnerships.requirements_title')}
                </p>
              </motion.div>
              <motion.ul variants={stagger} className="space-y-3">
                {requirementsItems.map((item, i) => (
                  <motion.li key={i} variants={fadeUp} className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-[var(--energy-green)] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

            {/* FAQs */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-1">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('partnerships.faq_title')}
                </p>
              </motion.div>
              <motion.div variants={fadeUp}>
                <Accordion type="single" collapsible className="space-y-2">
                  {faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="border border-border rounded-lg px-4 data-[state=open]:border-[var(--energy-green)]/30"
                    >
                      <AccordionTrigger className="font-heading font-medium text-sm py-3 text-left hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-3">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-[hsl(222,22%,10%)]">
        <div className="container-site">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="max-w-xl space-y-5"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white tracking-tight">
              {t('partnerships.cta_title')}
            </h2>
            <p className="text-[hsl(215,12%,65%)] leading-relaxed">
              {t('partnerships.cta_subtitle')}
            </p>
            <Button
              size="lg"
              className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary h-11"
              onClick={() => navigate('/contact')}
              data-testid="partnerships-final-cta"
            >
              {t('partnerships.cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
